import {
  ChangeDetectionStrategy,
  Component,
  inject,
  NgZone,
  Signal,
} from '@angular/core';
import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { AuthFacade } from '@lyceo/shared/auth/data-access';
import { Browser } from '@capacitor/browser';
import { EnvFunc } from '@lyceo/shared/util';
import { LoaderComponent } from '@lyceo/shared/ui';
import { Router } from '@angular/router';
import { UserFacade } from '@lyceo/homi/user/data-access';
import { AuthErrorComponent } from '@lyceo/shared/auth/feature-main';
import { PersonIdErrorComponent } from '@lyceo/homi/user/feature-permissions';
import { AppDeepLink, AppUrlService } from '@lyceo/shared/data-access';

@Component({
  imports: [
    IonApp,
    IonRouterOutlet,
    LoaderComponent,
    AuthErrorComponent,
    PersonIdErrorComponent,
  ],
  selector: 'homi-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly platform: Platform = inject(Platform);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly userFacade: UserFacade = inject(UserFacade);
  private readonly router: Router = inject(Router);
  private readonly appUrlService: AppUrlService = inject(AppUrlService);

  protected readonly showLoader: Signal<boolean> =
    this.userFacade.userShowLoader;
  protected readonly hasLoginError: Signal<boolean> =
    this.authFacade.hasLoginError;
  protected readonly hasPersonIdError: Signal<boolean> =
    this.userFacade.hasPersonIdError;
  protected platformString: string = Capacitor.getPlatform();

  constructor() {
    this.initializeApp();
  }

  private async initializeApp(): Promise<void> {
    await this.platform.ready();

    if (!this.platform.is('capacitor')) {
      return;
    }

    SplashScreen.hide();

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) =>
      this.urlOpenListener(event),
    );
  }

  private urlOpenListener(event: URLOpenListenerEvent): void {
    const deepLink: AppDeepLink | null = this.appUrlService.parseDeepLink(
      event.url,
    );
    if (!deepLink) {
      return;
    }

    this.ngZone.run(() => {
      if (deepLink.route === 'login') {
        this.authFacade.mobileLogoutFinish();
        return;
      }

      this.closeBrowser();

      if (deepLink.route === 'settings') {
        this.router.navigate(['/settings'], {
          queryParams: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            elo_success: deepLink.url.searchParams.get('elo_success'),
          },
        });
        return;
      }

      if (deepLink.route === 'home') {
        this.authFacade.mobileTryLogin(deepLink.url.search);
        return;
      }
    });
  }

  private closeBrowser(): void {
    if (Capacitor.getPlatform() === 'android') {
      EnvFunc.closeUri$.next();
    } else {
      Browser.close().catch(() => {
        /* an error is thrown when nothing can be closed, this is fine */
      });
    }
  }
}
